/**
 * LadderView — the workflow's steps, each read as a small workflow of its own:
 * TITLE → what it does → its facts, the same three bands the page header gives
 * the workflow itself. A `compact` variant renders just rung chips + names for
 * Overview/tooltips.
 *
 * Two things the earlier card could not say, both of which changed the shape:
 *
 *   1. WHAT THE STEP IS FOR. A step's name ("verify") is a handle, not an
 *      explanation. The description comes from the step's ROLE definition
 *      (`packages/agents/roles/<role>.md` frontmatter) — real authored text,
 *      not a per-name lookup table, so it stays true for a role this file has
 *      never heard of.
 *   2. HOW OFTEN IT FAILS. Cost, tokens and run count are all averages over
 *      `excellent_run_steps` — rows that only exist when a step actually RAN.
 *      A step that fails a quarter of its runs rendered identically to one that
 *      never fails. `failures` (from `loopStepFailures`) is the missing half,
 *      and it links to the runs behind it.
 *
 * Deliberately NOT interactive: role, model and rung are shown as facts, not
 * pickers. Changing a step's model is a gated act in this product — it goes
 * through `routeStepAction` as a candidate variant and lands only via a
 * promotion — so a dropdown here that wrote straight to `excellent_loop_steps`
 * would quietly route around the Optimization Harness gate. The tags point at
 * that control instead of replacing it.
 *
 * `"use client"` is load-bearing, not decoration: the tags carry `IconTooltip`
 * (Radix), and every other `IconTooltip` consumer in the package is a client
 * file for the same reason. Rendered from the server page without it, the
 * server emitted the tag's text with no trigger wrapper and the client emitted
 * the wrapper — a hydration mismatch that silently regenerated the whole
 * Pipeline subtree on every load. That boundary is also why a step's drill-in
 * link is a serializable STRING on the step (`runsHref`) rather than a
 * `(step) => string` prop: functions cannot cross a server→client boundary.
 *
 * UI-SPEC §3.3. Composes <Card> + <RungBadge> + <IconTooltip> (no raw lucide,
 * no native `title=`).
 */

import * as React from "react";
import { Card } from "../../card";
import { formatUsd } from "../../format";
import { ArrowRight, Bot, Cpu, ShieldCheck, TriangleAlert } from "../../icons";
import { IconTooltip } from "../icon-tooltip";
import { RungBadge } from "./rung-badge";

export interface LadderStepFailureBucket {
  key: string;
  label: string;
  hint: string;
  runs: number;
  latest_run_id: number | null;
}

export interface LadderStepFailures {
  finished: number;
  failed: number;
  buckets: LadderStepFailureBucket[];
}

export interface LadderStep {
  id: number;
  ordinal: number;
  name: string;
  rung: number;
  role: string | null;
  model: string | null;
  avg_cost?: number | null;
  avg_tokens?: number | null;
  /** One line on what this step is for — the role definition's `description`. */
  description?: string | null;
  /** Failure rollup for this step. `undefined` = not measured, not "clean". */
  failures?: LadderStepFailures;
  /**
   * Where this step's failure count leads. Precomputed by the caller (a plain
   * string, so it survives the server→client boundary); `null`/omitted renders
   * the count as static text rather than inventing a destination.
   */
  runsHref?: string | null;
}

function fmtTokens(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}

function pct0(n: number, of: number): string {
  return of > 0 ? `${Math.round((n / of) * 100)}%` : "—";
}

/** `claude-haiku-4-5` → `Haiku 4.5`. The id is an ADDRESS: it belongs in the
 *  tooltip, not as the value someone reads at a glance. The minor version is
 *  optional — `claude-sonnet-5` has only one number, and a pattern demanding
 *  two falls through to the raw slug. Anything unrecognized returns unchanged,
 *  which is ugly but never wrong. */
export function modelLabel(id: string): string {
  const m = /^claude-([a-z]+)-(\d+)(?:-(\d+))?(\[1m\])?/.exec(id);
  if (!m) return id;
  const name = m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1);
  return `${name} ${m[2]}${m[3] ? `.${m[3]}` : ""}${m[4] ? " · 1M" : ""}`;
}

/** A step fact: icon + value, with the label on the icon's tooltip — the same
 *  grammar the page header uses for the workflow's own facts. */
function StepTag({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: React.ReactNode;
  value: React.ReactNode;
  tone?: "primary";
}) {
  return (
    <span className="inline-flex items-center gap-1.5 type-subtext text-foreground">
      <IconTooltip label={label}>
        <span
          className={
            tone === "primary"
              ? "inline-flex cursor-default text-primary"
              : "inline-flex cursor-default text-muted-foreground"
          }
        >
          <Icon className="size-3.5" />
        </span>
      </IconTooltip>
      {value}
    </span>
  );
}

/** How often this step's runs don't finish, and a way into them. A count with
 *  nowhere to go is the thing this replaces, so the whole strip is the link. */
function StepFailures({
  failures,
  href,
}: {
  failures: LadderStepFailures;
  href?: string;
}) {
  if (failures.failed === 0) return null;

  const summary = (
    <>
      <span className="inline-flex items-center gap-1.5">
        <TriangleAlert className="size-3.5 shrink-0 text-danger" />
        <span className="text-foreground">{failures.failed} didn&rsquo;t finish</span>
      </span>
      <span className="text-muted-foreground">
        {pct0(failures.failed, failures.finished)} of {failures.finished}
      </span>
    </>
  );

  const body = (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 type-subtext">
      {summary}
      {href && <ArrowRight className="size-3 shrink-0 text-muted-foreground" aria-hidden />}
    </span>
  );

  // The causes ride the tooltip rather than the card: a four-way taxonomy is
  // the answer to "why", which is the next screen's question, not this one's.
  // The destination is named, because "124 didn't finish" reads as a link to a
  // LIST and there is no per-step run list to send anyone to — it opens one run,
  // so it has to say so before it is clicked.
  const newest = failures.buckets.reduce<number | null>(
    (a, b) => (b.latest_run_id != null && b.latest_run_id > (a ?? 0) ? b.latest_run_id : a),
    null,
  );
  const causes = (
    <span className="flex max-w-72 flex-col gap-1">
      {failures.buckets.map((b) => (
        <span key={b.key}>
          <span className="tabular-nums">{b.runs}</span> {b.label} — {b.hint}
        </span>
      ))}
      {href && newest != null && (
        <span className="opacity-70">Opens the most recent — run #{newest}</span>
      )}
    </span>
  );

  return (
    <div className="mt-auto border-t border-border/40 pt-2">
      <IconTooltip label={causes}>
        {href ? (
          <a
            href={href}
            className="block rounded-sm transition-colors hover:bg-foreground/[0.04] focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none"
          >
            {body}
          </a>
        ) : (
          <span className="block cursor-default">{body}</span>
        )}
      </IconTooltip>
    </div>
  );
}

export function LadderView({
  steps,
  variant = "full",
  gateLabel,
}: {
  steps: LadderStep[];
  variant?: "full" | "compact";
  gateLabel?: string;
}) {
  if (steps.length === 0) {
    return <p className="type-body text-muted-foreground">No steps defined for this workflow.</p>;
  }

  if (variant === "compact") {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {steps.map((s, i) => (
          <React.Fragment key={s.id}>
            {i > 0 && <ArrowRight className="h-3 w-3 text-muted-foreground" aria-hidden />}
            <span className="inline-flex items-center gap-1">
              <RungBadge rung={s.rung} showLabel={false} />
              <span className="type-subtext text-foreground">{s.name}</span>
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-stretch gap-2">
      {steps.map((s, i) => (
        <React.Fragment key={s.id}>
          {i > 0 && (
            <div className="flex items-center" aria-hidden>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          {/* `basis` + `grow` rather than a bare min-width: the card now carries
              a sentence, and a row of 150px columns broke every description
              onto six lines. */}
          <Card className="flex min-w-[220px] flex-1 basis-[260px] flex-col gap-2 p-3">
            <div className="flex items-start justify-between gap-2">
              <span className="type-body font-medium text-foreground">{s.name}</span>
              <span className="shrink-0 type-subtext tabular-nums text-muted-foreground">#{s.ordinal}</span>
            </div>

            {s.description && (
              <p className="line-clamp-3 type-subtext text-muted-foreground">{s.description}</p>
            )}

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {gateLabel && (
                <StepTag
                  icon={ShieldCheck}
                  tone="primary"
                  label={`Gated · ${gateLabel} — the workflow's rule, shared by every step`}
                  value="Gated"
                />
              )}
              {s.role && <StepTag icon={Bot} label="Role" value={s.role} />}
              {s.model && (
                <StepTag
                  icon={Cpu}
                  label={
                    <span className="flex max-w-64 flex-col gap-1">
                      <span>Model</span>
                      <span className="font-mono type-subtext opacity-70">{s.model}</span>
                      {/* Says WHY it isn't a picker. Read-only with no
                          explanation reads as an oversight; the real reason is
                          that a model change is a gated act here, and the
                          control that performs it is on this same card's
                          section. A tag that quietly wrote to the step would
                          route around the promotion gate. */}
                      <span className="opacity-70">
                        Changed by proposing a routing candidate below — it lands via a gated promotion,
                        never edited in place.
                      </span>
                    </span>
                  }
                  value={modelLabel(s.model)}
                />
              )}
              <RungBadge rung={s.rung} />
            </div>

            <div className="flex gap-3 type-subtext tabular-nums text-muted-foreground">
              <IconTooltip label="Average cost per run, last 7 days">
                <span className="cursor-default">{formatUsd(s.avg_cost)}</span>
              </IconTooltip>
              <IconTooltip label="Average tokens per run, last 7 days">
                <span className="cursor-default">{fmtTokens(s.avg_tokens)} tok</span>
              </IconTooltip>
            </div>

            {s.failures && <StepFailures failures={s.failures} href={s.runsHref ?? undefined} />}
          </Card>
        </React.Fragment>
      ))}
    </div>
  );
}
